import {
  Lightbulb,
  Megaphone,
  PhoneCall,
  Search,
  ShieldQuestion,
  Users2,
} from "lucide-react";
import { getModuleLabelForScenarioType } from "@/lib/modules/display-names";
import {
  ROLE_PLAY_TYPE_MODULE_LABELS,
  SCENARIO_TYPE_KEYS,
} from "@/lib/role-plays/scenario-types";
import { getTone } from "@/lib/ui/tones";

// Display labels for roleplay modules (UI calls these "modules"). This is the
// module column of lib/role-plays/scenario-types.js — `getRolePlayTypeLabel`
// resolves against a studio's enabled modules, so the module name is the one
// it is falling back from. For the name of a *single* scenario ("Objection",
// "Play") use ROLE_PLAY_TYPE_SCENARIO_LABELS from that same table.
export const ROLE_PLAY_TYPE_LABELS = ROLE_PLAY_TYPE_MODULE_LABELS;

// Per-module color accents. Color here is *categorization* (wayfinding), not
// decoration — each module owns one muted hue from the sanctioned status family
// (docs/design-guidelines.md §2). Same module → same color everywhere (list card
// tag, filter tab dot, detail header). The class strings behind each hue live in
// lib/ui/tones.js, so a module is re-toned by changing its hue name here.
function moduleTheme(Icon, hue) {
  const tone = getTone(hue);
  return {
    Icon,
    hue,
    tag: tone.pill,
    dot: tone.dot,
    tile: tone.tile,
    eyebrow: tone.text,
  };
}

export const ROLE_PLAY_THEME = {
  DISCOVERY: moduleTheme(Search, "action"),
  PITCH: moduleTheme(Megaphone, "violet"),
  OBJECTIONS: moduleTheme(ShieldQuestion, "warning"),
  PLAY: moduleTheme(Lightbulb, "emerald"),
  COLD_CALL: moduleTheme(PhoneCall, "teal"),
  MANAGEMENT: moduleTheme(Users2, "indigo"),
};

// A module we have no colour for reads as neutral, and one step quieter than
// the neutral ramp: an unrecognised type should recede behind the six that
// carry meaning.
//
// No `hue` here on purpose. The six real modules carry one because every field
// derives from it; this fallback's tile and eyebrow deliberately override what
// `getTone("gray")` would give (`bg-gray-50 text-gray-600`), so publishing a
// hue would invite a caller to re-derive them and silently undo the dimming.
export const DEFAULT_ROLE_PLAY_THEME = {
  Icon: Users2,
  tag: getTone("gray").pill,
  dot: getTone("gray").dot,
  tile: "bg-gray-100 text-gray-500",
  eyebrow: "text-gray-500",
};

export const getRolePlayTheme = (type) =>
  ROLE_PLAY_THEME[type] || DEFAULT_ROLE_PLAY_THEME;

export const getRolePlayTypeLabel = (type, enabledModules = []) =>
  getModuleLabelForScenarioType(
    enabledModules,
    type,
    ROLE_PLAY_TYPE_LABELS[type] ||
      (type ? type.toLowerCase().replace(/_/g, " ") : "")
  );

// getRolePlayTypeLabel for every type, keyed by type: what an assignment
// card's `moduleLabels` takes and a page groups its assignments under.
export const getRolePlayTypeLabels = (enabledModules = []) =>
  Object.fromEntries(
    SCENARIO_TYPE_KEYS.map((type) => [
      type,
      getRolePlayTypeLabel(type, enabledModules),
    ])
  );
